import { Box, Button, Tooltip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useCrossSectionDrawingHistory } from '~/hooks/undo/useCrossSectionDrawingHistory';
import { sdfOperationInFlightState } from '~/state/atoms/structureSet/roiAtoms';
import {
    crossSectionApplyRequestState,
    crossSectionPreviewReadyState,
} from '~/state/atoms/toolsAtoms';

const PILL_SX = {
    minWidth: 'auto',
    px: 1.5,
    py: 0.25,
    fontSize: 11,
    textTransform: 'none',
    borderRadius: 100,
    whiteSpace: 'nowrap',
} as const;

/** The Cross-Section tool's tray: Apply and Clear. */
export default function CrossSectionToolOptions() {
    const intl = useIntl();
    // Gated on the preview, not the drawn contours: the modifiers can clip the shape away.
    const previewReady = useRecoilValue(crossSectionPreviewReadyState);
    const isProcessing = useRecoilValue(sdfOperationInFlightState);
    const requestApply = useSetRecoilState(crossSectionApplyRequestState);
    const { hasDrawing, clear } = useCrossSectionDrawingHistory();
    const message = (id: string) => intl.formatMessage({ id });

    return (
        <Box sx={{ display: 'inline-flex', gap: 0.75 }}>
            <Tooltip
                title={message('crossSectionTool.apply.hint')}
                placement="bottom"
            >
                {/* A disabled Button fires no events, so the tooltip needs a wrapper. */}
                <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!previewReady || isProcessing}
                        onClick={() => requestApply((count) => count + 1)}
                        data-testid="cross-section-apply"
                        sx={PILL_SX}
                    >
                        <FormattedMessage id="crossSectionTool.apply" />
                    </Button>
                </Box>
            </Tooltip>
            <Tooltip
                title={message('crossSectionTool.clear.hint')}
                placement="bottom"
            >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={!hasDrawing || isProcessing}
                        onClick={clear}
                        data-testid="cross-section-clear"
                        sx={PILL_SX}
                    >
                        <FormattedMessage id="crossSectionTool.clear" />
                    </Button>
                </Box>
            </Tooltip>
        </Box>
    );
}
